/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { connect } from 'react-redux';

import type { NETGEM_API_V8_FEED } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import type { NETGEM_API_V8_SECTION } from '../../../libs/netgemLibrary/v8/types/Section';
import type { NetgemApiV8WidgetItemClick, TILE_CONFIG_TYPE } from '../../../libs/netgemLibrary/v8/types/WidgetConfig';
import type { Dispatch } from '../../../redux/types/types';
import type { SwipeEventData } from '../../swipeable/types';

import Localizer from '../../../helpers/localization';
import { getTileWidthPlusMargin } from '../../../helpers/ui/constants';
import { focusSiblingSection } from '../../../helpers/ui/section/navigation';
import { createSectionPagination } from '../../../helpers/ui/section/Pagination';
import { getTileConfig, getTileTypeClass } from '../../../helpers/ui/section/tile';
import { ignoreIfAborted } from '../../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import getTranslatedText from '../../../libs/netgemLibrary/v8/helpers/Lang';
import sendV8SectionFeedRequest from '../../../redux/netgemApi/actions/v8/feed';
import { PictoArrowLeft, PictoArrowRight } from '../../pictos/Element';
import Swipeable from '../../swipeable/swipeable';
import Item from '../item/Item';
import './Section.css';

type ReduxSectionDispatchToPropsType = {
  +localSendV8SectionFeedRequest: (section: NETGEM_API_V8_SECTION, signal?: AbortSignal) => Promise<NETGEM_API_V8_FEED>,
};

type DefaultProps = {
  +innerRef?: ((SectionView | null) => unknown) | null,
  +isInExploreModal?: boolean,
  +onItemClick?: NetgemApiV8WidgetItemClick,
};

type SectionPropType = {
  ...DefaultProps,
  +section: NETGEM_API_V8_SECTION,
};

type CompleteSectionPropType = {
  ...SectionPropType,
  ...ReduxSectionDispatchToPropsType,
};

type SectionStateType = {
  feed: NETGEM_API_V8_FEED,
  focusedItemIndex: number,
  itemCountPerPage: number,
  maxPageIndex: number,
  pageIndex: number,
};

const InitialState: Readonly<SectionStateType> = Object.freeze({
  feed: [],
  focusedItemIndex: 0,
  itemCountPerPage: -1,
  maxPageIndex: 0,
  pageIndex: 0,
});

// Number of pages actually rendered (even if only partially visible)
const PAGE_RENDER_COUNT = 2;

// Used to transform velocity in a page step when swiping a section
const PAGE_VELOCITY_STEP: number = 5;

class SectionView extends React.PureComponent<CompleteSectionPropType, SectionStateType> {
  abortController: AbortController;

  itemWidthPlusMargin: number;

  sectionName: string;

  slider: HTMLElement | null;

  sliderPositions: Array<number>;

  tileConfig: TILE_CONFIG_TYPE;

  static defaultProps: DefaultProps = {
    innerRef: null,
    isInExploreModal: false,
    onItemClick: undefined,
  };

  constructor(props: CompleteSectionPropType) {
    super(props);

    const {
      section,
      section: { id },
    } = props;

    this.abortController = new AbortController();
    this.sectionName = id;
    this.slider = null;
    this.sliderPositions = [];
    this.tileConfig = getTileConfig(section);
    this.itemWidthPlusMargin = getTileWidthPlusMargin(this.tileConfig.type);

    this.state = { ...InitialState };
  }

  componentDidMount() {
    const { innerRef } = this.props;

    if (innerRef) {
      // Expose the class instance
      innerRef(this);
    }

    window.addEventListener('resize', this.updateDimensions, { passive: true });
    this.loadData();
  }

  componentDidUpdate(prevProps: CompleteSectionPropType, prevState: SectionStateType) {
    const { feed, itemCountPerPage, pageIndex } = this.state;
    const { feed: prevFeed, itemCountPerPage: prevItemCountPerPage } = prevState;

    if (itemCountPerPage !== prevItemCountPerPage) {
      this.createPageList(prevItemCountPerPage);
      return;
    }

    if (prevFeed !== feed && feed.length > 0) {
      if (prevFeed.length !== feed.length) {
        // Feed has changed: re-render everything
        this.createPageList();
      }
    } else if (prevState.pageIndex !== pageIndex) {
      this.goToPage(pageIndex);
    }
  }

  componentWillUnmount() {
    const { abortController } = this;
    const { innerRef } = this.props;

    if (innerRef) {
      innerRef(null);
    }

    abortController.abort('Component SectionChannelGroup will unmount');

    window.removeEventListener('resize', this.updateDimensions, { passive: true });
  }

  loadData = () => {
    const { localSendV8SectionFeedRequest, section } = this.props;
    const {
      abortController: { signal },
    } = this;

    localSendV8SectionFeedRequest(section, signal)
      .then((feed: NETGEM_API_V8_FEED) => {
        signal.throwIfAborted();

        this.setState({ feed });
        this.updateDimensions();
      })
      .catch((error) => ignoreIfAborted(signal, error, () => this.setState({ feed: [] })));
  };

  createPageList: (previousItemCountPerPage?: number) => void = (previousItemCountPerPage) => {
    const { feed, itemCountPerPage, pageIndex: previousPageIndex } = this.state;
    const { itemWidthPlusMargin } = this;

    if (itemCountPerPage === -1) {
      return;
    }

    const { maxPageIndex, pageIndex, sliderPositions } = createSectionPagination(feed, itemWidthPlusMargin, itemCountPerPage, previousPageIndex, previousItemCountPerPage);

    this.sliderPositions = sliderPositions;
    this.setState({
      maxPageIndex,
      pageIndex,
    });
  };

  calculateItemCountPerPage = (): number => {
    const { itemWidthPlusMargin, slider } = this;

    if (!slider || !(slider.parentElement instanceof HTMLElement)) {
      return -1;
    }

    return Math.max(1, Math.floor(slider.parentElement.offsetWidth / itemWidthPlusMargin));
  };

  updateDimensions = () => {
    const itemCountPerPage = this.calculateItemCountPerPage();

    this.setState({ itemCountPerPage }, () => {
      if (itemCountPerPage === -1) {
        // Slider was not rendered yet, so let schedule another run
        setTimeout(this.updateDimensions, 0);
      }
    });
  };

  checkGetPageStep = (pageStep?: number): number => (typeof pageStep === 'number' && pageStep > 1 ? pageStep : 1);

  handleNextButtonOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    this.goToNextPage();
  };

  goToNextPage = (pageStep?: number) => {
    const { maxPageIndex, pageIndex } = this.state;

    const step = this.checkGetPageStep(pageStep);
    this.goToPage(Math.min(pageIndex + step, maxPageIndex));
  };

  handlePrevButtonOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    this.goToPreviousPage();
  };

  goToPreviousPage = (pageStep?: number) => {
    const { pageIndex } = this.state;

    const step = this.checkGetPageStep(pageStep);
    this.goToPage(Math.max(0, pageIndex - step));
  };

  goToPage = (pageIndex: number) => {
    const { slider, sliderPositions } = this;

    if (!slider || pageIndex < 0 || pageIndex >= sliderPositions.length) {
      return;
    }

    const posX = sliderPositions[pageIndex];
    slider.style.transform = `translateX(${posX}px) translateZ(0)`;

    this.setState({ pageIndex });
  };

  handleGridKeyDown = (event: SyntheticKeyboardEvent<HTMLElement>): void => {
    const { slider } = this;
    const { key } = event;

    if (!slider) {
      return;
    }

    const focusableItems = Array.from(slider.querySelectorAll<HTMLElement>('.sectionItem')).filter((el) => el.offsetWidth > 0 && el.offsetHeight > 0);

    const totalItems = focusableItems.length;
    if (totalItems === 0) {
      return;
    }

    const activeElement = document.activeElement;
    const currentIndex = focusableItems.findIndex((item) => item === activeElement || item.contains(activeElement));

    if (currentIndex === -1) {
      return;
    }

    // Inter-section navigation
    if (key === 'ArrowUp' || key === 'ArrowDown') {
      focusSiblingSection(slider, key);
      return;
    }

    // Intra-section navigation
    let nextIndex = currentIndex;

    switch (key) {
      case 'ArrowLeft':
        nextIndex = Math.max(0, currentIndex - 1);
        break;
      case 'ArrowRight':
        nextIndex = Math.min(totalItems - 1, currentIndex + 1);
        break;
      default:
        return;
    }

    if (nextIndex !== currentIndex) {
      event.preventDefault();
      event.stopPropagation();

      this.setState({ focusedItemIndex: nextIndex }, () => {
        const targetElement = focusableItems[nextIndex];
        if (targetElement) {
          targetElement.focus();
        }
      });
    }
  };

  handleOnSwiping = (eventData: SwipeEventData) => {
    const { deltaX } = eventData;
    const { maxPageIndex, pageIndex } = this.state;
    const { slider, sliderPositions } = this;

    if (!slider || maxPageIndex === 0) {
      return;
    }

    const posX = sliderPositions[pageIndex];
    const newPosX = posX + deltaX;

    slider.style.transform = `translateX(${newPosX}px) translateZ(0)`;
  };

  handleOnSwiped = (eventData: SwipeEventData) => {
    const { dir, velocity } = eventData;
    const { maxPageIndex } = this.state;

    if (maxPageIndex === 0) {
      return;
    }

    const pageStep = Math.ceil(velocity / PAGE_VELOCITY_STEP);

    if (dir === 'Left') {
      this.goToNextPage(pageStep);
    } else if (dir === 'Right') {
      this.goToPreviousPage(pageStep);
    }
  };

  handleItemFocused: (itemIndex: number) => void = (itemIndex) => {
    const { itemCountPerPage, pageIndex } = this.state;

    // Check if focused item belongs to a different page than the currently active page
    const itemPage = Math.floor(itemIndex / itemCountPerPage);

    if (itemPage !== pageIndex) {
      this.goToPage(itemPage);
    }
  };

  renderTiles = (): Array<React.Node> => {
    const { isInExploreModal, onItemClick } = this.props;
    const {
      feed,
      feed: { length: itemCount },
      focusedItemIndex,
      itemCountPerPage,
      pageIndex,
    } = this.state;
    const { tileConfig } = this;

    const indexEnd = Math.min((pageIndex + PAGE_RENDER_COUNT) * itemCountPerPage, itemCount);
    const tiles: Array<React.Node> = [];

    for (let i = 0; i < indexEnd; ++i) {
      const { [i]: item } = feed;

      tiles.push(
        <Item
          isInExploreModal={isInExploreModal}
          item={item}
          itemIndex={i}
          key={item.id}
          onItemClick={onItemClick}
          onItemFocused={this.handleItemFocused}
          tabIndex={i === focusedItemIndex ? 0 : -1}
          tileConfig={tileConfig}
        />,
      );
    }

    return tiles;
  };

  render(): React.Node {
    const { section } = this.props;
    const { feed, maxPageIndex, pageIndex } = this.state;
    const {
      tileConfig: { imageLayout, type },
    } = this;

    if (feed.length === 0) {
      // Do not display empty section
      return null;
    }

    // Tile type to CSS class conversion (e.g. 'gemtv.big' -> 'gemtv big')
    const tileTypeClass = getTileTypeClass(type);

    // Display only visible pages
    const tiles = this.renderTiles();

    const prevButton =
      pageIndex > 0 ? (
        <div className='navigationButton previous' onClick={this.handlePrevButtonOnClick}>
          <PictoArrowLeft forceHoverEffect />
        </div>
      ) : null;

    const nextButton =
      pageIndex < maxPageIndex ? (
        <div className='navigationButton next' onClick={this.handleNextButtonOnClick}>
          <PictoArrowRight forceHoverEffect />
        </div>
      ) : null;

    const sectionTitle = getTranslatedText(section.title, Localizer.language);

    return (
      <div className={clsx('section', 'channelGroupSection', tileTypeClass, imageLayout)} id={`section_${this.sectionName}`}>
        <div className='sectionChannelGroupHeader'>
          <div className='sectionTitleContainer'>{sectionTitle}</div>
        </div>
        <Swipeable onSwiped={this.handleOnSwiped} onSwiping={this.handleOnSwiping} trackMouse>
          <div
            aria-label={sectionTitle}
            className='slider'
            onKeyDownCapture={this.handleGridKeyDown}
            ref={(instance: HTMLElement | null) => {
              this.slider = instance;
            }}
            role='grid'
          >
            {tiles}
          </div>
        </Swipeable>
        {prevButton}
        {nextButton}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch): ReduxSectionDispatchToPropsType => {
  return {
    localSendV8SectionFeedRequest: (section: NETGEM_API_V8_SECTION, signal?: AbortSignal): Promise<NETGEM_API_V8_FEED> => dispatch(sendV8SectionFeedRequest(section, null, signal)),
  };
};

const Section: React.ComponentType<SectionPropType> = connect(null, mapDispatchToProps)(SectionView);

export { SectionView };
export default Section;
